import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Mail, FileText, FileSpreadsheet, Loader2, Calendar } from "lucide-react";

// Date helpers — YYYY-MM-DD
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PRESETS = [
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 },
  { key: "ytd", label: "Year to date", days: null },
  { key: "custom", label: "Custom range", days: null },
];

export default function Statements() {
  const today = useMemo(() => new Date(), []);
  const default30 = useMemo(() => new Date(Date.now() - 30 * 86400_000), []);

  const [preset, setPreset] = useState("30d");
  const [fromDate, setFromDate] = useState(toISO(default30));
  const [toDate, setToDate] = useState(toISO(today));
  const [mode, setMode] = useState("all");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [autoOptOut, setAutoOptOut] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setAutoOptOut(!!(data?.preferences?.monthly_statement_opt_out));
    }).catch(() => {});
  }, []);

  const toggleAutoStatement = async (val) => {
    try {
      await api.patch("/wallet/wallet-preferences/monthly-statement", { monthly_statement_opt_out: val });
      setAutoOptOut(val);
      toast.success(val
        ? "You won't receive the monthly auto-statement."
        : "Enabled — you'll get a statement on the 1st of each month.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/wallet/statement?format=json&from_date=${fromDate}&to_date=${toDate}&mode=${mode}`
      );
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle preset selection
  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return;
    const now = new Date();
    let start;
    if (key === "30d") start = new Date(Date.now() - 30 * 86400_000);
    else if (key === "90d") start = new Date(Date.now() - 90 * 86400_000);
    else if (key === "ytd") start = new Date(now.getFullYear(), 0, 1);
    setFromDate(toISO(start));
    setToDate(toISO(now));
  };

  const downloadFile = async (format) => {
    setDownloading(format);
    try {
      const res = await fetch(
        `${API}/wallet/statement?format=${format}&from_date=${fromDate}&to_date=${toDate}&mode=${mode}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FinVault-Statement-${fromDate}_to_${toDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded.`);
    } catch (e) {
      toast.error(e?.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const emailStatement = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/wallet/statement/email", {
        from_date: fromDate,
        to_date: toDate,
        mode,
      });
      if (data.email_sent) {
        toast.success(`Statement emailed — ${data.count} transactions.`);
      } else {
        toast.warning("Statement generated, but email delivery isn't configured.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailing(false);
    }
  };

  const money = (v) =>
    (v < 0 ? "-" : "") +
    "$" +
    Math.abs(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <AppLayout>
      <div className="text-eyebrow text-white/55">Records</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 text-white" data-testid="statements-heading">
        Account statements
      </h1>
      <p className="text-white/60 mt-2 text-sm max-w-2xl">
        Generate a downloadable statement for any period up to 366 days. Delivered as a signed PDF or a spreadsheet-friendly CSV — and localized to your saved language & currency.
      </p>

      {/* Filters */}
      <div className="mt-8 fv-card p-6 md:p-7" data-testid="statements-filters">
        <div className="flex flex-wrap gap-2" role="tablist">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              data-testid={`statements-preset-${p.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                preset === p.key
                  ? "bg-[#B58957] text-[#0A130E]"
                  : "bg-white/[0.06] text-white/70 hover:bg-white/[0.10]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-white/60 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> From
            </Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPreset("custom"); }}
              data-testid="statements-from-date"
              className="mt-1 bg-transparent border-white/15 text-white"
            />
          </div>
          <div>
            <Label className="text-xs text-white/60 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> To
            </Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPreset("custom"); }}
              data-testid="statements-to-date"
              className="mt-1 bg-transparent border-white/15 text-white"
            />
          </div>
          <div>
            <Label className="text-xs text-white/60">Mode</Label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              data-testid="statements-mode-select"
              className="mt-1 w-full h-10 px-3 rounded-md bg-transparent border border-white/15 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B58957]"
            >
              <option value="all" className="text-black">All (Live + Demo)</option>
              <option value="live" className="text-black">Live only</option>
              <option value="demo" className="text-black">Demo only</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            onClick={fetchPreview}
            disabled={loading}
            variant="outline"
            className="border-white/15 text-white hover:bg-white/[0.08]"
            data-testid="statements-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Refresh preview
          </Button>
          <Button
            onClick={() => downloadFile("pdf")}
            disabled={downloading === "pdf"}
            className="bg-[#B58957] text-[#0A130E] hover:bg-[#c19b6c]"
            data-testid="statements-download-pdf"
          >
            {downloading === "pdf" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
          <Button
            onClick={() => downloadFile("csv")}
            disabled={downloading === "csv"}
            variant="outline"
            className="border-white/15 text-white hover:bg-white/[0.08]"
            data-testid="statements-download-csv"
          >
            {downloading === "csv" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Download CSV
          </Button>
          <Button
            onClick={emailStatement}
            disabled={emailing}
            variant="outline"
            className="border-white/15 text-white hover:bg-white/[0.08]"
            data-testid="statements-email-me"
          >
            {emailing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Email it to me
          </Button>
        </div>
      </div>

      {/* Auto-statement opt-in */}
      <div className="mt-4 fv-card p-4 md:p-5 flex items-center justify-between gap-4" data-testid="auto-statement-card">
        <div>
          <div className="text-sm font-semibold text-white">Monthly auto-statement</div>
          <div className="text-xs text-white/60 mt-1">We'll email you a signed PDF + CSV on the 1st of every month at 09:00 UTC.</div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!autoOptOut}
            onChange={(e) => toggleAutoStatement(!e.target.checked)}
            data-testid="auto-statement-toggle"
            className="w-4 h-4 accent-[#10B981]"
          />
          <span className="text-xs text-white/70">{autoOptOut ? "Off" : "On"}</span>
        </label>
      </div>

      {/* Summary + list */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <SumCard label="Opening" value={money(preview?.opening ?? 0)} color="text-white" testid="statements-sum-opening" />
        <SumCard label="Money in" value={"+" + money(preview?.money_in ?? 0)} color="text-[#4E7F5F]" testid="statements-sum-in" />
        <SumCard label="Money out" value={"-" + money(preview?.money_out ?? 0)} color="text-[#B85847]" testid="statements-sum-out" />
        <SumCard label="Closing" value={money(preview?.closing ?? 0)} color="text-white" testid="statements-sum-closing" />
      </div>

      <div className="mt-8 fv-card overflow-hidden" data-testid="statements-list">
        <div className="px-6 py-4 border-b border-white/10 text-sm text-white/60 flex items-center justify-between">
          <span>
            {preview
              ? `${preview.count} transactions between ${fromDate} and ${toDate}`
              : "Loading…"}
          </span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-white/60">Loading…</div>
        ) : !preview || preview.count === 0 ? (
          <div className="p-10 text-center text-white/60">
            No transactions in this range. Try a wider window.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left bg-white/[0.04] text-white/60 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 md:px-6 py-3">Date</th>
                  <th className="px-4 md:px-6 py-3">Type</th>
                  <th className="px-4 md:px-6 py-3">Description</th>
                  <th className="px-4 md:px-6 py-3 text-right">Amount</th>
                  <th className="px-4 md:px-6 py-3 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {preview.items.slice(0, 100).map((it) => (
                  <tr key={it.tx_id} className="border-t border-white/10 hover:bg-white/[0.03]">
                    <td className="px-4 md:px-6 py-3 text-white/70 whitespace-nowrap font-mono text-xs">
                      {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 md:px-6 py-3 text-white/80 capitalize">{(it.type || "").replace("_", " ")}</td>
                    <td className="px-4 md:px-6 py-3 text-white/70">
                      <div>{it.description || "—"}</div>
                      {it.counterparty ? (
                        <div className="text-white/45 text-xs mt-0.5">{it.counterparty}</div>
                      ) : null}
                    </td>
                    <td className={`px-4 md:px-6 py-3 text-right font-mono ${it.amount >= 0 ? "text-[#4E7F5F]" : "text-[#B85847]"}`}>
                      {it.amount >= 0 ? "+" : "-"}${Math.abs(it.amount).toFixed(2)} {it.currency}
                    </td>
                    <td className="px-4 md:px-6 py-3 text-right font-mono text-white/80">
                      ${it.running.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.count > 100 && (
              <div className="px-6 py-3 text-xs text-white/50 border-t border-white/10">
                Showing first 100 of {preview.count}. Full list is in the downloaded PDF/CSV.
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function SumCard({ label, value, color, testid }) {
  return (
    <div className="fv-card p-5" data-testid={testid}>
      <div className="text-eyebrow text-white/55">{label}</div>
      <div className={`font-display text-2xl md:text-3xl font-bold mt-2 tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
